import React, { useState } from 'react';
import {
    AppBar,
    Box,
    Toolbar,
    Button,
    Container,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Stack,
    useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, MenuItem, Divider, Fade, Collapse } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about-us' },
    { name: 'Products', path: null },
    { name: 'Gallery', path: '/gallery' }
];

const productItems = [
    { name: 'Cobble Stone', path: '/cobble-stone' },
    { name: 'Paving Stone', path: '/paving-stone' },
    { name: 'Wall Cladding', path: '/wall-cladding' },
    { name: 'Benches', path: '/benches' },
    { name: 'Kerb Stone', path: '/kerb-stone' },
    { name: 'Pergola', path: '/pergola' },
    { name: 'Cobble Stone Lying (Fixing) and path', path: '/cobble-stone-lying' }
];

const Header = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleMobileProductsToggle = () => {
        setMobileProductsOpen(!mobileProductsOpen);
    };

    const navigate = useNavigate();

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const drawer = (
        <Box sx={{ textAlign: 'center' }}>
            <List>
                {navItems.map((item) => (
                    <React.Fragment key={item.name}>
                        <ListItem disablePadding sx={{ display: 'block' }}>
                            {item.name === 'Products' ? (
                                <>
                                    <Button
                                        onClick={handleMobileProductsToggle}
                                        sx={{
                                            textAlign: 'left',
                                            width: '100%',
                                            color: 'inherit',
                                            justifyContent: 'space-between',
                                            px: 3,
                                            py: 1.5
                                        }}
                                    >
                                        <ListItemText primary={item.name} />
                                        {mobileProductsOpen ? <ExpandLess /> : <ExpandMore />}
                                    </Button>
                                    <Collapse in={mobileProductsOpen} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding>
                                            {productItems.map((product) => (
                                                <Button
                                                    key={product.name}
                                                    component={Link}
                                                    to={product.path} // Ensure path is valid
                                                    onClick={handleDrawerToggle}
                                                    sx={{
                                                        pl: 6,
                                                        textAlign: 'left',
                                                        width: '100%',
                                                        color: 'text.secondary',
                                                        justifyContent: 'flex-start',
                                                        py: 1,
                                                        fontSize: '0.9rem'
                                                    }}
                                                >
                                                    <ListItemText primary={product.name} />
                                                </Button>
                                            ))}
                                        </List>
                                    </Collapse>
                                </>
                            ) : (
                                <Button
                                    component={Link}
                                    to={item.path}
                                    onClick={handleDrawerToggle}
                                    sx={{
                                        textAlign: 'left',
                                        width: '100%',
                                        color: 'inherit',
                                        justifyContent: 'flex-start',
                                        px: 3,
                                        py: 1.5
                                    }}
                                >
                                    <ListItemText primary={item.name} />
                                </Button>
                            )}
                        </ListItem>
                        <Divider />
                    </React.Fragment>
                ))}
            </List>
        </Box>
    );

    return (
        <AppBar position="sticky" color="default" sx={{ bgcolor: 'white', boxShadow: 'none', py: 1 }}>
            <Container>
                {isMobile ? (
                    <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
                        {/* Mobile Logo Centered */}
                        <Box component={Link} to="/" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', }}>
                            <img src={require('../assets/4mhenterprises.png')} alt="4MH Enterprises" style={{ height: '90px', objectFit: 'contain' }} />
                        </Box>

                        {/* Mobile Buttons Row */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Button
                                onClick={handleDrawerToggle}
                                startIcon={<MenuIcon />}
                                size='small'
                                sx={{
                                    bgcolor: 'black',
                                    color: 'white',
                                    boxShadow: 1,
                                    borderRadius: 1,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    px: 2,
                                    '&:hover': { bgcolor: '#f5f5f5' }
                                }}
                            >
                                Menu
                            </Button>
                            <Button
                                component={Link}
                                to="/contact-us"
                                variant="contained"
                                disableElevation
                                size='small'
                                sx={{
                                    bgcolor: '#25D5B2',
                                    color: 'white',
                                    borderRadius: 1,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    px: 2,
                                    '&:hover': { bgcolor: '#1fc2a1' },
                                }}
                            >
                                Contact Now
                            </Button>
                        </Stack>
                    </Stack>
                ) : (
                    <Toolbar disableGutters>
                        {/* Logo */}
                        <Box component={Link} to="/" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                            <img src={require('../assets/4mhenterprises.png')} alt="4MH Enterprises" style={{ height: '70px', objectFit: 'cover' }} />
                        </Box>

                        {/* Desktop Navigation */}
                        <Stack direction="row" spacing={2.5} alignItems="center">
                            {navItems.map((item) => (
                                <Box key={item.name}
                                    onMouseLeave={item.name === 'Products' ? handleMenuClose : undefined}
                                    sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
                                >
                                    <Button
                                        component={item.path ? Link : 'button'}
                                        to={item.path}
                                        onMouseOver={item.name === 'Products' ? handleMenuOpen : undefined}
                                        sx={{
                                            color: 'text.primary',
                                            fontWeight: 500,
                                            fontSize: '16px',
                                            '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            textTransform: 'none'
                                        }}
                                    >
                                        {item.name}
                                        {item.name === 'Products' && <AddIcon sx={{ fontSize: 16 }} />}
                                    </Button>
                                    {item.name === 'Products' && (
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={open}
                                            onClose={handleMenuClose}
                                            MenuListProps={{
                                                onMouseLeave: handleMenuClose,
                                                sx: { py: 0 }
                                            }}
                                            TransitionComponent={Fade}
                                            sx={{
                                                '& .MuiPaper-root': {
                                                    borderRadius: 1,
                                                    boxShadow: 3,
                                                    mt: 1,
                                                    bgcolor: 'white',
                                                    minWidth: 200,
                                                }
                                            }}
                                        >
                                            {productItems.map((product, index) => (
                                                <Box key={product.name}>
                                                    <MenuItem
                                                        component={Link}
                                                        to={product.path}
                                                        onClick={handleMenuClose}
                                                        sx={{
                                                            fontSize: '14px',
                                                            fontWeight: 500,
                                                            py: 1,
                                                            color: 'text.primary',
                                                            textDecoration: 'none',
                                                            '&:hover': { color: 'primary.main', bgcolor: '#f5f5f5' }
                                                        }}
                                                    >
                                                        {product.name}
                                                    </MenuItem>
                                                    {index < productItems.length - 1 && <Divider />}
                                                </Box>
                                            ))}
                                        </Menu>
                                    )}
                                </Box>
                            ))}
                            <Button
                                component={Link}
                                to="/contact-us"
                                variant="contained"
                                disableElevation
                                sx={{
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    borderRadius: '0px',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    px: 4,
                                    py: 1.5,
                                    '&:hover': { bgcolor: '#1fc2a1' },
                                }}
                            >
                                Contact Now
                            </Button>
                        </Stack>
                    </Toolbar>
                )}
            </Container>

            {/* Mobile Drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile.
                }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: '100%', mt: '210px', height: 'calc(80% - 160px)', boxShadow: 'none', borderTop: '1px solid #eee' }, // Adjust top margin to sit below header roughly
                }}
            >
                {drawer}
            </Drawer>
        </AppBar>
    );
};

export default Header;
